import { registerFragment } from '@redwoodjs/web/apollo';

registerFragment(gql`
  fragment DocumentAttributes on Document {
    id
    title
    url
    type
    createdAt
    uploadedBy {
      user {
        id
        firstName
        lastName
      }
    }
    documentTemplateId
    documentTemplate {
      name
    }
  }
`);

registerFragment(gql`
  fragment ExpenseAttributes on Expense {
    id
    description
    businessEntity {
      id
      name
    }
    businessEntityId
    accountLevel
    expenseType {
      id
      name
      receivablesAccountId
      payablesAccountId
    }
    billLineItem {
      billId
    }
    category
    chargePer
    quantity
    unitAmount
    currencyAmount
    totalAmount
    parentExpenseId
    purchaseOrderId
    buySellOrderId
    salesOrderId
    workOrderId
    proportion
    childrenExpenses {
      id
      bills {
        id
        slug
        type
        status
        attachmentType
      }
    }
    includedInDlvPrice
    parentExpenseId
    locked

    ledgerAccount {
      id
      accountName
      accountType
      accountNumber
    }

    linkedLineItems {
      id
      product {
        description
      }
    }
    expenseLineItems {
      id
      lineItemId
      amount
    }
    manualAllocationLineItemSnapshot
  }
`);

registerFragment(gql`
  fragment ExpenseFinanceEntities on Expense {
    bills {
      id
      slug
      type
      status
      attachmentType
    }
  }
`);

registerFragment(gql`
  fragment LotAttributes on Lot {
    id
    slug
    status
    quantity
    weight
    metrics {
      unitsAvailable
      landedCost
      grossProfit
      directCostTotal
    }
    receiveDate
    warehouseId

    lotSupplierSources {
      businessEntity {
        id
        name
      }
      place {
        id
        description
      }
      type
    }

    productionRunComponents {
      id
      createdAt
      type

      productionRun {
        id
        slug
        completedAt
      }
    }

    fieldTicketIds
    harvestLocations
  }
`);

registerFragment(gql`
  fragment LotSourceAttributes on Lot {
    lotSupplierSources {
      businessEntity {
        id
        name
      }
      place {
        id
        description
      }
      type
    }

    fieldTicketIds
    harvestLocations
  }
`);

registerFragment(gql`
  fragment PalletTagAttributes on PalletTag {
    id
    lotId
    # status
    initialQuantity
    cartonQuantity
    tagNumber
    tagLayout {
      id
      name
      schema
      tagType
    }
    weight
    initialWeight
    printed
    warehouseLocation {
      id
      slug
      description
      tags
    }
    quantityAdjustments {
      delta
      weightDelta
      initialDelta
      initialWeightDelta
      adjustmentType
      createdAt
      lotQuantityAdjustment {
        id
      }
      adjustedBy {
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`);

registerFragment(gql`
  fragment NoteAttributes on Note {
    id
    text
    createdAt
    external
    documentTypes
    membership {
      user {
        id
        firstName
        lastName
        email
      }
    }
    customDocumentTypes {
      documentTemplateId
    }
  }
`);

registerFragment(gql`
  fragment OrganizationAttributes on Organization {
    id
    label
    productDescriptionTemplate
    logoUrl
    salesOrderDocumentPrefix
    closedAt
    clerkId
  }
`);

registerFragment(gql`
  fragment PlaceAttributes on Place {
    id
    description

    streetAddress1
    streetAddress2

    city
    state
    addressCountry
    postalCode

    addressRoles

    phone
  }
`);

registerFragment(gql`
  fragment ProductAttributes on Product {
    id
    description
    countryOfOrigin
    weightPerInventoryUnit
    cartonsPerPallet
    grade
    variableWeight
    productCode
    organic
    commodity {
      name
    }
    commodityStyle {
      name
    }
    commoditySize {
      name
    }
    commodityUnit {
      name
    }
    commodityLabel {
      name
    }
    gtinCode
    customFields
  }
`);

export const OrderLineItemRelationsFields = gql`
  fragment OrderLineItemRelationsFields on LineItem {
    id

    parentLineItem {
      enteredByWeight
    }

    palletTagsOnLineItem {
      palletTag {
        id
        tagNumber
        cartonQuantity
        weight
      }
      quantity
      weight
    }

    lot {
      id
      status
      grade
      slug
      warehouseId
      product {
        productId: id
        description
        commodity {
          name
        }
      }
    }

    ediProductMapping {
      id
      ediProductDescription
      ediProductId
    }

    product {
      productId: id
      organic
      grade
      countryOfOrigin
      commodityStyle {
        name
      }
      commoditySize {
        name
      }
      commodityLabel {
        name
      }
    }
  }
`;
registerFragment(OrderLineItemRelationsFields);

registerFragment(gql`
  fragment ReceiptAdjustmentAttributes on ReceiptAdjustment {
    id
    amount
    type
    reason
    memo

    ledgerEntryGroup {
      lines {
        account {
          name
        }
      }
    }

    balancingAccount {
      accountName
    }
  }
`);

export const LineItemAttributes = gql`
  fragment LineItemAttributes on LineItem {
    id
    salesOrderId
    purchaseOrderId
    enteredByWeight
    unitsOrdered
    weightOrdered
    unitsReceived
    weightReceived
    unitsPicked
    weightPicked
    unitsShipped
    weightShipped
    currencyPrice
    buyPrice
    buyCurrencyPrice
    sellPrice
    sellCurrencyPrice
    profit
    parentLineItemId
    parentLineItem {
      enteredByWeight
    }
    external
    deliveredPrice
    deliveredCurrencyPrice

    palletTagsOnLineItem {
      palletTag {
        id
        tagNumber
        cartonQuantity
        initialQuantity
        initialWeight
        weight
      }
      quantity
      weight
    }

    lot {
      status
      id
      grade
      slug
      metrics {
        grossProfit
        landedCost
      }
      initialQuantity
      initialWeight
      warehouseId
      quantity

      lotOrderSources {
        workOrderId
      }

      product {
        productId: id
        description
        landedCost
        weightPerInventoryUnit
        commodity {
          name
        }
      }
    }
    ediProductMapping {
      id
      ediProductDescription
      ediProductId
    }
    product {
      description
      organic
      weightPerInventoryUnit
      variableWeight
      landedCost
      productId: id
      cartonsPerPallet
      countryOfOrigin
      commodity {
        name
        id
      }
      commodityStyle {
        name
      }
      commoditySize {
        name
      }
      commodityUnit {
        name
      }
      commodityLabel {
        name
      }
      grade
    }
    freeformProductDescription
    aiMetadata
  }
`;
registerFragment(LineItemAttributes);

registerFragment(gql`
  fragment PaymentBillRelation on Payment {
    parentPaymentId
    ledgerAccountPath
    billId
    bill {
      id
      type
      slug
      status
      reference
      invoiceDate
      totalAmountDue
      totalPaid
      salesOrder {
        id
        slug
        customerPo
      }
      purchaseOrder {
        id
        slug
        vendorPo
      }
      buySellOrder {
        id
        slug
        customerPo
        vendorSo
      }
      growerProduct {
        id
        slug
        fieldTicketId
      }
    }
  }
`);

registerFragment(gql`
  fragment BillAttributes on ViewBill {
    id
    slug
    type
    status
    paymentTerms
    reference
    counterPartyId
    counterPartyName
    counterPartyContactName
    invoiceDate
    dueDate
    orderType
    orderId
    orderSlug
    orderFlagged
    orderShipDate
    totalAmountDue
    totalPaid
    totalAdjustments
    amountRemaining
    balancingAccountId
    balancingAccount {
      id
      accountNumber
      accountName
    }
    attachedLabels {
      labelDefinition {
        label
        color
      }
    }
    qboConnected
    qboThirdPartyId
    qboSyncStatus
    qboSyncError
    qboSyncErrorSolution
    qboSyncedAt
    qboSyncStartedAt
    qboTriggeredByEmail
  }
`);
